import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { LogOut, LayoutDashboard, FilePlus, FileText, Settings, Shield, KeyRound, CheckSquare, CalendarDays,
  Menu, X, TrendingUp, Handshake, Megaphone, DollarSign, UserCircle,
  Signature, Zap,
  ChevronDown, Car, Caravan, Bike, Ship, Package,
  ClipboardList, ClipboardCheck, UserCheck, Users, Mail, CreditCard, ChevronRight, Building2,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Menu blueprint
// -----------------------------------------------------------------------------
const PROPOSAL_ITEMS = [
  { to: "/proposals",             label: "Proposals",           icon: FileText,      testid: "nav-proposals" },
  { to: "/proposals/new",         label: "New Proposals",       icon: FilePlus,      testid: "nav-new-proposal" },
  { to: "/proforma",              label: "Proforma",            icon: TrendingUp,    testid: "nav-proforma" },
];

const RATE_CARD_ITEMS = [
  { to: "/portfolio/automotive",  label: "Automotive",  icon: Car,     testid: "nav-portfolio-automotive" },
  { to: "/portfolio/rv",          label: "RV",          icon: Caravan, testid: "nav-portfolio-rv" },
  { to: "/portfolio/powersports", label: "Powersports", icon: Bike,    testid: "nav-portfolio-powersports" },
  { to: "/portfolio/marine",      label: "Marine",      icon: Ship,    testid: "nav-portfolio-marine" },
];
const MARKETING_ITEMS = [
  { label: "Rate Cards", icon: CreditCard, testid: "nav-marketing-rate-cards", children: RATE_CARD_ITEMS },
  { to: "/marketing/my-orders",   label: "My Orders",           icon: ClipboardList, testid: "nav-marketing-my-orders" },
];
const CRM_ITEMS = [
  { to: "/crm/contacts", label: "Contacts", icon: Users, testid: "nav-crm-contacts" },
  { to: "/crm/dealers", label: "Dealers", icon: Building2, testid: "nav-crm-dealers" },
  { to: "/marketing/email-campaigns", label: "Email Marketing", icon: Mail, testid: "nav-marketing-email-campaigns" },
];

// Settings dropdown — user profile settings + admin surfaces (only shown to
// admins). Consolidates what used to be four separate navbar entries so the
// top row stays uncluttered.
const buildSettingsItems = ({ isAdmin, showItem }) => {
  const items = [
    { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
  ];
  if (isAdmin && showItem("admin"))     items.push({ to: "/admin/users",     label: "Admin",     icon: Shield,   testid: "nav-admin" });
  if (isAdmin && showItem("admin"))     items.push({ to: "/admin/onboarding/priority", label: "Priority", icon: Zap, testid: "nav-admin-priority" });
  if (isAdmin && showItem("admin"))     items.push({ to: "/admin/agents",    label: "Agents",    icon: Signature, testid: "nav-admin-agents" });
  if (isAdmin && showItem("materials")) items.push({ to: "/admin/marketing", label: "Materials", icon: Package,  testid: "nav-admin-marketing" });
  if (isAdmin && showItem("sso"))       items.push({ to: "/admin/sso",       label: "SSO",       icon: KeyRound, testid: "nav-admin-sso" });
  return items;
};

// Commissions module is hosted in the sibling `dealercomm` workspace and
// surfaced here as a single iframe entry — its internal nav lives on the side
// inside the iframe, so no dropdown is needed on the parent nav.

// -----------------------------------------------------------------------------
// Desktop dropdown — hover-open + click-to-toggle (idempotent: click while
// hover-opened keeps it open; click on a closed trigger opens it).
// Keyboard-accessible, closes on outside click.
// -----------------------------------------------------------------------------
function NavDropdown({ label, icon: Icon, items, testid }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const rootRef = useRef(null);
  const closeTimer = useRef(null);
  // Track how the menu got opened AND mirror the open state into a ref so the
  // click handler can read the latest values synchronously — mouseenter is a
  // continuous event whose setState has not committed yet when the immediate
  // click handler runs.
  const openedByHover = useRef(false);
  const openRef = useRef(false);

  const setOpenSafe = (next) => {
    const val = typeof next === "function" ? next(openRef.current) : next;
    openRef.current = val;
    setOpen(val);
  };

  // Highlight the trigger when the user is on any of its children
  const flat = items.flatMap((it) => (it.children ? it.children : [it]));
  const isActiveGroup = flat.some((it) => location.pathname.startsWith(it.to));
  const [subOpen, setSubOpen] = useState(null);

  // Delay close on mouse-leave so users can trace a diagonal to the panel.
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setOpenSafe(false);
      openedByHover.current = false;
    }, 120);
  };
  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };

  const onHoverOpen = () => {
    cancelClose();
    if (!openRef.current) {
      openedByHover.current = true;
      setOpenSafe(true);
    }
  };

  const onTriggerClick = () => {
    // Read the ref (synchronously up-to-date) — the state closure may still
    // hold `open===false` when a mouseenter -> click race occurs on real
    // pointer devices, so relying on `open` here would incorrectly close the
    // menu that mouseenter just opened.
    if (openedByHover.current) {
      openedByHover.current = false;
      // Ensure the menu remains open (mouseenter has already opened it).
      setOpenSafe(true);
      return;
    }
    setOpenSafe((v) => !v);
    openedByHover.current = false;
  };

  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpenSafe(false);
        openedByHover.current = false;
      }
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpenSafe(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={onHoverOpen}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={onTriggerClick}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`px-3 py-2 rounded-sm text-sm font-medium transition-colors inline-flex items-center gap-2 ${
          isActiveGroup ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
        }`}
        data-testid={testid}
      >
        <Icon size={15} /> {label}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full pt-1 z-50 min-w-[220px]"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          data-testid={`${testid}-panel`}
        >
          <div className="rounded-sm border border-[#E9ECEF] bg-white shadow-lg">
            {items.map((it) => it.children ? (
              <div key={it.label} className="relative" onMouseEnter={() => setSubOpen(it.label)} onMouseLeave={() => setSubOpen(null)} data-testid={`${it.testid}-group`}>
                <button type="button" onClick={() => setSubOpen((s) => (s === it.label ? null : it.label))} aria-haspopup="menu" aria-expanded={subOpen === it.label}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${it.children.some((c) => location.pathname.startsWith(c.to)) ? "bg-[#D4AF37]/15 text-[#0B132B]" : "text-[#0B132B] hover:bg-[#F8F9FA]"}`}
                  data-testid={it.testid} role="menuitem">
                  <it.icon size={14} className="shrink-0" /> {it.label}
                  <ChevronRight size={12} className="ml-auto opacity-60" />
                </button>
                {subOpen === it.label && (
                  <div className="absolute left-full top-0 pl-1 min-w-[200px] z-50" data-testid={`${it.testid}-panel`}>
                    <div className="rounded-sm border border-[#E9ECEF] bg-white shadow-lg overflow-hidden">
                      {it.children.map((c) => (
                        <NavLink key={c.to} to={c.to} onClick={() => { setOpenSafe(false); openedByHover.current = false; setSubOpen(null); }}
                          className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#D4AF37]/15 text-[#0B132B]" : "text-[#0B132B] hover:bg-[#F8F9FA]"}`}
                          data-testid={c.testid} role="menuitem">
                          <c.icon size={14} className="shrink-0" /> {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => { setOpenSafe(false); openedByHover.current = false; }}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-[#D4AF37]/15 text-[#0B132B]" : "text-[#0B132B] hover:bg-[#F8F9FA]"
                  }`
                }
                data-testid={it.testid}
                role="menuitem"
              >
                <it.icon size={14} className="shrink-0" /> {it.label}
                {it.badge > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center" data-testid={`${it.testid}-badge`}>{it.badge}</span>}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Mobile: sectioned accordion (open by default so it's discoverable)
// -----------------------------------------------------------------------------
function MobileSection({ title, icon: Icon, items, onNavigate, testid }) {
  return (
    <div className="pt-2" data-testid={testid}>
      <div className="px-4 pb-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D] flex items-center gap-1.5">
        <Icon size={12}/> {title}
      </div>
      {items.flatMap((it) => (it.children ? [{ ...it, header: true }, ...it.children.map((c) => ({ ...c, indent: true }))] : [it])).map((it) => it.header ? (
        <div key={it.label} className="px-4 pt-2 pb-1 text-[11px] font-bold text-[#0B132B] flex items-center gap-2" data-testid={`${it.testid}-mobile`}><it.icon size={13} /> {it.label}</div>
      ) : (
        <NavLink
          key={it.to} to={it.to} onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 ${it.indent ? "pl-9 pr-4" : "px-4"} py-3 rounded-sm text-sm font-medium transition-colors ${
              isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
            }`
          }
          data-testid={`${it.testid}-mobile`}
        >
          <it.icon size={16} /> {it.label}
          {it.badge > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center">{it.badge}</span>}
        </NavLink>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// User menu (name + role trigger → My Profile, Calendar, Tasks, Sign out)
// -----------------------------------------------------------------------------
function UserMenu({ user, roleLabel, items, badge, onLogout }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const timer = useRef(null);
  const show = () => { if (timer.current) clearTimeout(timer.current); setOpen(true); };
  const hide = () => { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 150); };
  useEffect(() => {
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown); document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, []);
  const initials = (user.name || user.email || "?").split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  return (
    <div ref={rootRef} className="relative hidden sm:block" onMouseEnter={show} onMouseLeave={hide}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}
        className={`flex items-center gap-2.5 pl-1.5 pr-2.5 py-1 rounded-sm transition-colors ${open ? "bg-[#0B132B]/5" : "hover:bg-[#0B132B]/5"}`} data-testid="navbar-user-menu">
        <span className="relative w-8 h-8 rounded-full bg-[#0B132B] text-[#D4AF37] text-xs font-black grid place-items-center">{initials}
          {badge > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold grid place-items-center" data-testid="nav-tasks-badge">{badge}</span>}
        </span>
        <span className="flex flex-col text-left leading-tight">
          <span className="text-sm font-semibold text-[#0B132B]" data-testid="navbar-user-name">{user.name}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6C757D]">{roleLabel}</span>
        </span>
        <ChevronDown size={12} className={`text-[#6C757D] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full pt-1 z-50 min-w-[220px]" data-testid="navbar-user-menu-panel">
          <div className="rounded-sm border border-[#E9ECEF] bg-white shadow-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[#E9ECEF] bg-[#F8F9FA]"><div className="text-xs font-bold text-[#0B132B] truncate">{user.name}</div><div className="text-[11px] text-[#6C757D] truncate">{user.email}</div></div>
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} role="menuitem"
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#D4AF37]/15 text-[#0B132B]" : "text-[#0B132B] hover:bg-[#F8F9FA]"}`} data-testid={it.testid}>
                <it.icon size={14} className="shrink-0" /> {it.label}
                {it.badge > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center">{it.badge}</span>}
              </NavLink>
            ))}
            <button type="button" onClick={onLogout} role="menuitem" className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-50 border-t border-[#E9ECEF]" data-testid="navbar-logout-button"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Navbar
// -----------------------------------------------------------------------------
export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const perms = user?.permissions || [];
  const navHidden = new Set(user?.nav_hidden || []);
  const hasPerm = (p) => isAdmin || perms.includes(p);
  const canSeeOnboarding = isAdmin || user?.role === "agency_admin"
    || hasPerm("onboarding.production.manage")
    || hasPerm("onboarding.testing.manage")
    || hasPerm("onboarding.install.assign");
  const canSeeCommissions = isAdmin || user?.role === "agency_admin" || hasPerm("commissions.view");
  const showItem = (key) => !navHidden.has(key);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [taskAttention, setTaskAttention] = useState(0);
  useEffect(() => {
    if (!user) return;
    const pull = () => api.get("/tasks").then(({ data }) => setTaskAttention(data?.attention || 0)).catch(() => {});
    pull();
    const t = setInterval(pull, 60000);
    return () => clearInterval(t);
  }, [user]);
  const MY_BUSINESS_ITEMS = [
    { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
    { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks", badge: taskAttention },
  ];
  const ACCOUNT_ITEMS = [{ to: "/profile", label: "My Profile", icon: UserCircle, testid: "nav-my-profile" }, ...MY_BUSINESS_ITEMS];
  const showCrm = !!user;
  const TasksLink = ({ mobile }) => mobile
    ? <MobileSection title="My Account" icon={UserCircle} items={ACCOUNT_ITEMS} onNavigate={closeMobile} testid="nav-my-business-mobile" />
    : null;

  const isSupport = user?.role === "support";
  const roleLabel =
    user?.role === "admin" ? "Admin" :
    user?.role === "agency_admin" ? "Agency Admin" :
    isSupport ? "Support / Production" : "Agent";

  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
      isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
    }`;

  const doLogout = async () => { await logout(); nav("/login"); };
  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <header
        className="sticky top-0 z-40 border-b border-[#E9ECEF] bg-white/85 backdrop-blur-xl"
        data-testid="app-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="flex items-center gap-3 shrink-0" data-testid="navbar-brand">
            <BrandLogo size="sm" />
          </Link>

          {/* Desktop nav */}
          {isSupport ? (
            <nav className="hidden lg:flex items-center gap-1" data-testid="support-nav">
              <NavLink to="/onboarding" className={linkCls} data-testid="nav-onboarding">
                <span className="inline-flex items-center gap-2"><ClipboardCheck size={15} /> Onboarding Pipeline</span>
              </NavLink>
              <TasksLink />
              <NavLink to="/settings" className={linkCls} data-testid="nav-settings">
                <span className="inline-flex items-center gap-2"><Settings size={15} /> My profile</span>
              </NavLink>
            </nav>
          ) : (
          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">
              <span className="inline-flex items-center gap-2">
                <LayoutDashboard size={15} /> Dashboard
              </span>
            </NavLink>

            <NavDropdown
              label="Proposals"
              icon={FileText}
              items={PROPOSAL_ITEMS}
              testid="nav-proposals-menu"
            />
            <NavDropdown
              label="Marketing"
              icon={Megaphone}
              items={MARKETING_ITEMS}
              testid="nav-marketing"
            />
            {showCrm && <NavDropdown label="CRM" icon={Users} items={CRM_ITEMS} testid="nav-crm" />}

            {canSeeOnboarding && showItem("onboarding") && (
              <NavDropdown
                label="Dealer Signup"
                icon={Handshake}
                items={[
                  { to: "/dealers",           label: "Sign Up",         icon: Handshake,      testid: "nav-signup" },
                  { to: "/dealers/existing",  label: "Existing Dealers", icon: Users,         testid: "nav-existing-dealers" },
                  { to: "/onboarding",        label: "Onboarding",       icon: ClipboardCheck, testid: "nav-onboarding" },
                ]}
                testid="nav-dealer-signup"
              />
            )}

            <TasksLink />
            {canSeeCommissions && <NavLink to="/commissions" className={linkCls} data-testid="nav-commissions">
              <span className="inline-flex items-center gap-2">
                <DollarSign size={15} /> Commissions
              </span>
            </NavLink>}

            {user?.role === "agent" && (
              <NavLink to="/onboarding/mine" className={linkCls} data-testid="nav-my-onboardings">
                <span className="inline-flex items-center gap-2">
                  <ClipboardCheck size={15} /> My Dealers
                </span>
              </NavLink>
            )}

            <NavDropdown
              label="Settings"
              icon={Settings}
              items={buildSettingsItems({ isAdmin, showItem })}
              testid="nav-settings-dropdown"
            />

            {user?.role === "agency_admin" && (
              <NavLink to="/agency/users" className={linkCls} data-testid="nav-agency-users">
                <span className="inline-flex items-center gap-2">
                  <Shield size={15} /> My agency
                </span>
              </NavLink>
            )}
          </nav>
          )}

          <div className="flex items-center gap-3">
            {user && <NotificationBell />}
            {user && (
              <UserMenu user={user} roleLabel={roleLabel} items={ACCOUNT_ITEMS} badge={taskAttention} onLogout={doLogout} />
            )}
            {/* Mobile menu trigger */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-[#0B132B]/20 text-[#0B132B]"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              data-testid="navbar-mobile-toggle"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
            onClick={closeMobile}
            data-testid="navbar-mobile-backdrop"
          />
          <aside
            className="lg:hidden fixed top-[57px] right-0 bottom-0 z-40 w-[85vw] max-w-sm bg-white border-l border-[#E9ECEF] shadow-2xl overflow-y-auto"
            data-testid="navbar-mobile-drawer"
          >
            <div className="p-4 border-b border-[#E9ECEF] bg-[#F8F9FA]">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">{roleLabel}</div>
              <div className="text-base font-bold text-[#0B132B]">{user?.name}</div>
              <div className="text-xs text-[#6C757D] mt-0.5">{user?.email}</div>
            </div>
            <nav className="p-3 space-y-1">
              {isSupport && (
                <NavLink to="/onboarding" onClick={closeMobile} className={linkCls} data-testid="mobile-nav-onboarding">
                  <span className="inline-flex items-center gap-2"><ClipboardCheck size={15} /> Onboarding Pipeline</span>
                </NavLink>
              )}
              <TasksLink mobile />
              {!isSupport && (<>
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                    isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
                  }`
                }
                onClick={closeMobile}
                data-testid="nav-dashboard-mobile"
              >
                <LayoutDashboard size={16} /> Dashboard
              </NavLink>

              <MobileSection title="Proposals" icon={FileText} items={PROPOSAL_ITEMS} onNavigate={closeMobile} testid="nav-proposals-mobile" />
              <MobileSection title="Marketing" icon={Megaphone} items={MARKETING_ITEMS} onNavigate={closeMobile} testid="nav-marketing-mobile" />
              {showCrm && <MobileSection title="CRM" icon={Users} items={CRM_ITEMS} onNavigate={closeMobile} testid="nav-crm-mobile" />}

              {canSeeOnboarding && showItem("onboarding") && (
                <MobileSection
                  title="Dealer Signup" icon={Handshake}
                  items={[
                    { to: "/dealers",           label: "Sign Up",          icon: Handshake,      testid: "nav-signup" },
                    { to: "/dealers/existing",  label: "Existing Dealers", icon: Users,          testid: "nav-existing-dealers" },
                    { to: "/onboarding",        label: "Onboarding",       icon: ClipboardCheck, testid: "nav-onboarding" },
                  ]}
                  onNavigate={closeMobile} testid="nav-dealer-signup-mobile"
                />
              )}
              {canSeeCommissions && <NavLink
                to="/commissions"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                    isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
                  }`
                }
                onClick={closeMobile}
                data-testid="nav-commissions-mobile"
              >
                <DollarSign size={16} /> Commissions
              </NavLink>}
              {canSeeOnboarding && showItem("onboarding") && (
                <NavLink
                  to="/onboarding"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                      isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
                    }`
                  }
                  onClick={closeMobile}
                  data-testid="nav-onboarding-mobile"
                  style={{display: "none"}}
                >
                  <ClipboardCheck size={16} /> Onboarding
                </NavLink>
              )}
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                    isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
                  }`
                }
                onClick={closeMobile}
                data-testid="nav-settings-mobile"
              >
                <Settings size={16} /> Settings
              </NavLink>

              {isAdmin && (
                <NavLink
                  to="/admin/users"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                      isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
                    }`
                  }
                  onClick={closeMobile}
                  data-testid="nav-admin-mobile"
                >
                  <Shield size={16} /> Admin
                </NavLink>
              )}
              {isAdmin && (
                <NavLink
                  to="/admin/marketing"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                      isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
                    }`
                  }
                  onClick={closeMobile}
                  data-testid="nav-admin-marketing-mobile"
                >
                  <Package size={16} /> Materials
                </NavLink>
              )}
              {isAdmin && (
                <NavLink
                  to="/admin/sso"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                      isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
                    }`
                  }
                  onClick={closeMobile}
                  data-testid="nav-admin-sso-mobile"
                >
                  <KeyRound size={16} /> SSO
                </NavLink>
              )}
              {user?.role === "agency_admin" && (
                <NavLink
                  to="/agency/users"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                      isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
                    }`
                  }
                  onClick={closeMobile}
                  data-testid="nav-agency-users-mobile"
                >
                  <Shield size={16} /> My agency
                </NavLink>
              )}
              </>)}
              <button
                type="button"
                onClick={() => { closeMobile(); doLogout(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors mt-4 border-t border-[#E9ECEF] pt-4"
                data-testid="navbar-logout-mobile"
              >
                <LogOut size={16} /> Sign out
              </button>
            </nav>
          </aside>
        </>
      )}
    </>
  );
}
